import React, { useState, useEffect } from 'react';
import StatsCard from '../../../../components/StatsCards';
import QuestionMarkTooltip from '../../../../components/QuestionMarkTooltip';
import { transformPaymentData } from './utils/paymentDataTransformer';
import { axiosInstance } from '../../../../routes/axiosInstance';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  TrophyIcon,
  ArrowPathIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

/** Sum numeric fields across the two horizon rows of a track; preserve
 *  non-numeric metadata from the first available horizon row.
 *  Returns null if both inputs are null. */
function mergeHorizons({ intraday, d1 }) {
  if (!intraday && !d1) return null;
  const ref = intraday || d1;
  const numFields = [
    'daily_payment', 'league_payment', 'league_bonus_payment',
    'month_payment', 'total_payment', 'outstanding_payment',
    'nr_scores', 'nr_days_missed',
  ];
  const out = { ...ref };
  for (const f of numFields) {
    out[f] = (intraday?.[f] || 0) + (d1?.[f] || 0);
  }
  return out;
}

function effectiveThreshold(...rows) {
  const raw = rows.find((row) => row?.effective_min_payout_threshold != null)
    ?.effective_min_payout_threshold;
  const parsed = Number(raw);
  return raw !== undefined && raw !== null && Number.isFinite(parsed)
    ? parsed
    : 1000;
}

const shownThreshold = (data) => data.minPayoutThreshold ?? 1000;
const thresholdTooltip = (data) => {
  const threshold = shownThreshold(data);
  if (threshold === 0) {
    return 'Carry-over is disabled for this payment scope because its minimum payout threshold is €0.';
  }
  return `A monthly total below €${threshold} is carried over to the next month. For a market in a payment group, that threshold applies to the combined total across the group's markets.`;
};

// League badge component
const LeagueBadge = ({ league }) => {
  const config = {
    elite: { label: 'Elite', bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', border: 'border-yellow-300 dark:border-yellow-700' },
    challenger: { label: 'Challenger', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300', border: 'border-blue-300 dark:border-blue-700' },
    runner_up: { label: 'Runner-Up', bg: 'border border-gray-300 dark:border-gray-600', text: 'text-gray-800 dark:text-gray-300', border: 'border-gray-300 dark:border-gray-600' },
    disqualified: { label: 'Disqualified', bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', border: 'border-red-300 dark:border-red-700' },
    none: { label: 'No League', bg: 'border border-gray-300 dark:border-gray-600', text: 'text-gray-600 dark:text-gray-400', border: 'border-gray-300 dark:border-gray-600' },
  };

  const { label, bg, text, border } = config[league] || config.none;

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${bg} ${text} border ${border}`}>
      {label}
    </span>
  );
};

// Track payment card component
const TrackPaymentCard = ({ title, data }) => {
  if (!data) {
    return (
      <div className="bg-surface dark:bg-gray-870 rounded-lg p-5 shadow-sm border border-gray-200/70 dark:border-gray-600/70">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No data available for this track
        </div>
      </div>
    );
  }

  const { ranking, payments } = data;
  const hasDetailedData = ranking.league != null;

  // Simplified view when detailed breakdown is not available
  if (!hasDetailedData) {
    return (
      <div className="bg-surface dark:bg-gray-870 rounded-lg p-5 shadow-sm border border-gray-200/70 dark:border-gray-600/70">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
        <div className="flex items-center justify-between py-3 bg-surface dark:bg-gray-870 rounded-lg px-3">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Track Total</span>
          <div className="text-right">
            <span className="text-lg font-bold text-brand-600 dark:text-brand-400">€{payments.trackTotal.toFixed(2)}</span>
            {payments.outstandingPayment > 0 && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                €{payments.monthTotal.toFixed(2)} earned + €{payments.outstandingPayment.toFixed(2)} outstanding
              </div>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
          Detailed breakdown not available for this period
        </p>
      </div>
    );
  }

  return (
    <div className="bg-surface dark:bg-gray-870 rounded-lg p-5 shadow-sm border border-gray-200/70 dark:border-gray-600/70">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        <LeagueBadge league={ranking.league} />
      </div>

      {/* Ranking info */}
      <div className="mb-4 p-3 bg-surface dark:bg-gray-870 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className="text-sm text-gray-600 dark:text-gray-400">Monthly Rank</span>
            <QuestionMarkTooltip tooltipText="Rank based on average daily skill scores (RMSE for deterministic, MWI for probabilistic) throughout the month." size="sm" />
          </div>
          <span className="text-xl font-bold text-gray-900 dark:text-gray-100">
            #{ranking.monthlyRank} <span className="text-sm font-normal text-gray-500">of {ranking.totalForecasters}</span>
          </span>
        </div>
      </div>

      {/* Payment breakdown */}
      <div className="space-y-2">
        <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-1">
            <span className="text-sm text-gray-600 dark:text-gray-400">League Rewards</span>
            <QuestionMarkTooltip tooltipText="Monthly rewards based on league placement. Elite (ranks 1-5), Challenger (ranks 6-10), and Runner-Up (rank 11) receive different prize shares." size="sm" />
          </div>
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">€{payments.leagueBonus.toFixed(2)}</span>
        </div>
        {title === 'Deterministic (RMSE)' && (
          <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">Peak Error Bonus</span>
              <QuestionMarkTooltip tooltipText="€100 bonus awarded to the Elite League forecaster with the lowest peak error (deterministic track only). Split equally if tied." size="sm" />
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">€{payments.peakErrorBonus.toFixed(2)}</span>
          </div>
        )}
        <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-1">
            <span className="text-sm text-gray-600 dark:text-gray-400">Daily Rewards</span>
            <QuestionMarkTooltip tooltipText="Daily prizes distributed to top 5 forecasters each day based on their daily rank." size="sm" />
          </div>
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">€{payments.dailyRewardsTotal.toFixed(2)}</span>
        </div>
        {payments.outstandingPayment > 0 && (
          <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">Outstanding (prev. month)</span>
              <QuestionMarkTooltip tooltipText={thresholdTooltip(data)} size="sm" />
            </div>
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">+€{payments.outstandingPayment.toFixed(2)}</span>
          </div>
        )}
        <div className="flex items-center justify-between py-3 bg-surface dark:bg-gray-870 rounded-lg px-3 mt-3">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Track Total</span>
          <div className="text-right">
            <span className="text-lg font-bold text-brand-600 dark:text-brand-400">€{payments.trackTotal.toFixed(2)}</span>
            {payments.outstandingPayment > 0 && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                €{payments.monthTotal.toFixed(2)} earned + €{payments.outstandingPayment.toFixed(2)} outstanding
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Per-horizon breakdown */}
      {data.perHorizon && (
        <div className="mt-4 grid grid-cols-2 gap-3">
          {['intraday', 'd1'].map((h) => {
            const row = data.perHorizon[h];
            if (!row) return null;
            const label = h === 'intraday' ? 'Intraday' : 'Day-Ahead';
            return (
              <div key={h} className="rounded-md bg-surface dark:bg-gray-870 p-2.5">
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">{label}</div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">League</span>
                  <span className="text-xs font-medium">€{Number(row.league_payment || 0).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Daily</span>
                  <span className="text-xs font-medium">€{Number(row.daily_payment || 0).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between mt-1 pt-1 border-t border-gray-200/70 dark:border-gray-600/70">
                  <span className="text-xs font-semibold">Total</span>
                  <span className="text-xs font-bold text-brand-600">€{Number(row.month_payment || 0).toFixed(2)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function PaymentsReport({
  selectedUser,
  setSelectedUser,
  selectedYear,
  setSelectedYear,
  selectedMonthNum,
  setSelectedMonthNum,
  participants,
  participantsLoading,
  monthOptions,
  yearOptions,
  hideUserSelector = false,
  resourceId,
  blurSensitive = false,
}) {
  const [showDailyDetails, setShowDailyDetails] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [horizonFilter, setHorizonFilter] = useState('all');
  // Set when "All horizons" returns nothing and we silently fall back to
  // legacy (pre-refactor) rows — surfaces a small badge to explain the view.
  const [autoLegacy, setAutoLegacy] = useState(false);

  // Fetch payment data from API when selection or horizon filter changes
  useEffect(() => {
    const mk = (track, horizon) => axiosInstance.get('/user/payment/list', {
      params: {
        user: selectedUser,
        resource: resourceId,
        year: selectedYear,
        month: selectedMonthNum,
        track,
        horizon_category: horizon,
      },
    });
    // Single-horizon fetch (Intraday / D+1 / D+N / Legacy):
    // 2 parallel fetches (one per track), single-row payload per track,
    // no per-horizon sub-card grid.
    const fetchSingle = async (horizon) => {
      const [det, prob] = await Promise.all([
        mk('deterministic', horizon),
        mk('probabilistic', horizon),
      ]);
      const detData = det.data?.data?.[0] || null;
      const probData = prob.data?.data?.[0] || null;
      if (!detData && !probData) return null;
      const transformed = transformPaymentData(detData, probData);
      transformed.perHorizon = null;
      transformed.minPayoutThreshold = effectiveThreshold(detData, probData);
      return transformed;
    };

    // Multi-horizon "All horizons" fetch: 4 parallel fetches and the
    // per-horizon sub-cards. Falls back to Legacy when all 4 are empty so
    // historical (pre-refactor) months still render without forcing the
    // user to pick the Legacy pill manually.
    const fetchAll = async () => {
      const [detIntra, detD1, probIntra, probD1] = await Promise.all([
        mk('deterministic', 'intraday'),
        mk('deterministic', 'd1'),
        mk('probabilistic', 'intraday'),
        mk('probabilistic', 'd1'),
      ]);
      const byHorizon = {
        deterministic: {
          intraday: detIntra.data?.data?.[0] || null,
          d1: detD1.data?.data?.[0] || null,
        },
        probabilistic: {
          intraday: probIntra.data?.data?.[0] || null,
          d1: probD1.data?.data?.[0] || null,
        },
      };
      const anyData = Object.values(byHorizon).some(
        (track) => track.intraday || track.d1
      );
      if (!anyData) return null;
      const aggDet = mergeHorizons(byHorizon.deterministic);
      const aggProb = mergeHorizons(byHorizon.probabilistic);
      const transformed = transformPaymentData(aggDet, aggProb);
      transformed.perHorizon = byHorizon;
      transformed.minPayoutThreshold = effectiveThreshold(
        detIntra.data?.data?.[0], detD1.data?.data?.[0],
        probIntra.data?.data?.[0], probD1.data?.data?.[0]
      );

      // Participation is a per-horizon metric (each horizon expects one
      // submission per day). The aggregate participation shown for
      // "All horizons" is the mean of each horizon's individual rate, so
      // the figure stays bounded by 100% and the X/Y subtext keeps using
      // calendar-day units instead of summed horizon-days.
      const horizonRows = ['intraday', 'd1']
        .map((h) => byHorizon.deterministic[h] || byHorizon.probabilistic[h])
        .filter(Boolean);
      if (horizonRows.length > 0) {
        const mean = (fn) =>
          horizonRows.reduce((s, r) => s + fn(r), 0) / horizonRows.length;
        const avgRate = mean((r) =>
          r.nr_expected_scores > 0
            ? (r.nr_scores / r.nr_expected_scores) * 100
            : 0
        );
        transformed.eligibility = {
          ...transformed.eligibility,
          participationRate: parseFloat(avgRate.toFixed(1)),
          daysSubmitted: Math.round(mean((r) => r.nr_scores || 0)),
          daysMissed: Math.round(mean((r) => r.nr_days_missed || 0)),
        };
        transformed.period = {
          ...transformed.period,
          daysInMonth: Math.round(mean((r) => r.nr_expected_scores || 0)),
        };
      }
      return transformed;
    };

    const fetchPaymentData = async () => {
      if (!selectedUser || !resourceId || !selectedYear || !selectedMonthNum) {
        setPaymentData(null);
        setAutoLegacy(false);
        return;
      }
      setLoading(true);
      setError(null);
      setAutoLegacy(false);
      try {
        let data = null;
        if (horizonFilter === 'all') {
          data = await fetchAll();
          if (!data) {
            // Silent fallback to legacy for pre-refactor months.
            data = await fetchSingle('legacy');
            if (data) setAutoLegacy(true);
          }
        } else {
          data = await fetchSingle(horizonFilter);
        }
        if (!data) {
          setPaymentData(null);
          setError('No payment data found for the selected period.');
        } else {
          setPaymentData(data);
        }
      } catch (err) {
        console.error('Error fetching payment data:', err);
        setError(err?.response?.data?.message || err.message || 'Failed to fetch payment data');
        setPaymentData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentData();
  }, [selectedUser, resourceId, selectedYear, selectedMonthNum, horizonFilter]);

  const HORIZON_OPTIONS = [
    {value: 'all', label: 'All horizons'},
    {value: 'intraday', label: 'Intraday'},
    {value: 'd1', label: 'D+1'},
    {value: 'dn', label: 'D+N'},
    {value: 'legacy', label: 'Legacy (Pre June 26)'},
  ];

  // Helper to format league name for display
  const formatLeagueName = (league) => {
    if (!league) return 'N/A';
    return league.charAt(0).toUpperCase() + league.slice(1).replace('_', ' ');
  };

  return (
    <section className="py-4 sm:py-6">
      {/* Horizon filter pill bar */}
      {selectedUser && (
        <div className="mb-4 flex items-center flex-wrap gap-2">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400 mr-1">Horizon:</span>
          {HORIZON_OPTIONS.map((opt) => {
            const active = horizonFilter === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setHorizonFilter(opt.value)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-brand-600 text-white border-brand-600'
                    : 'bg-surface dark:bg-gray-870 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
          {autoLegacy && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
              Showing legacy payment (Pre June 26) for this period
            </span>
          )}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <ArrowPathIcon className="h-12 w-12 text-gray-400 mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Loading Payment Data</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Please wait...</p>
        </div>
      )}

      {/* No user selected state */}
      {!loading && !selectedUser && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Forecaster Selected</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Please select a forecaster to view their payment breakdown.</p>
        </div>
      )}

      {/* Error state */}
      {!loading && selectedUser && error && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-yellow-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Payment Data</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
        </div>
      )}

      {/* Payment data display */}
      {!loading && selectedUser && paymentData && (
        <>
          {/* Eligibility Alert Banner */}
          <div className={`mb-4 px-4 py-2 rounded-lg border grid grid-cols-2 gap-4 text-sm ${
            paymentData.eligibility.isEligibleForDailyRewards && paymentData.eligibility.isEligibleForLeagueRewards
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
              : !paymentData.eligibility.isEligibleForDailyRewards && !paymentData.eligibility.isEligibleForLeagueRewards
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
          }`}>
            <div className="flex items-center justify-center gap-1.5">
              {paymentData.eligibility.isEligibleForDailyRewards ? (
                <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
              ) : (
                <XCircleIcon className="h-4 w-4 text-red-600 dark:text-red-400" />
              )}
              <span className={paymentData.eligibility.isEligibleForDailyRewards ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>
                {paymentData.eligibility.isEligibleForDailyRewards ? 'Eligible for Daily Rewards' : 'Not eligible for Daily Rewards'}
              </span>
              <QuestionMarkTooltip tooltipText="Eligible if at least 1 day submitted. Daily prizes are distributed to top 5 forecasters each day." size="sm" />
            </div>
            <div className="flex items-center justify-center gap-1.5">
              {paymentData.eligibility.isEligibleForLeagueRewards ? (
                <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
              ) : (
                <XCircleIcon className="h-4 w-4 text-red-600 dark:text-red-400" />
              )}
              <span className={paymentData.eligibility.isEligibleForLeagueRewards ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>
                {paymentData.eligibility.isEligibleForLeagueRewards
                  ? 'Eligible for League Rewards'
                  : `Not eligible for League Rewards (${paymentData.eligibility.daysMissed} days missed)`}
              </span>
              <QuestionMarkTooltip tooltipText="Eligible if 5 or fewer days missed. Monthly rewards based on league placement (Elite, Challenger, Runner-Up)." size="sm" />
            </div>
          </div>

          {/* Outstanding Payment Banner */}
          {paymentData.outstandingPayment > 0 && (
            <div className="mb-4 px-4 py-3 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2">
                <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <span className="text-sm text-blue-700 dark:text-blue-300">
                  €{paymentData.outstandingPayment.toFixed(2)} of your total payment is outstanding from previous month(s)
                </span>
                <QuestionMarkTooltip tooltipText={thresholdTooltip(paymentData)} size="sm" />
              </div>
            </div>
          )}

          {/* Payment Summary Stats */}
          <StatsCard
            stats={[
              {
                title: 'Total Monthly Payment',
                count: `€${paymentData.totalPayment.toFixed(2)}`,
                note: paymentData.outstandingPayment > 0
                  ? `€${paymentData.monthPayment.toFixed(2)} earned + €${paymentData.outstandingPayment.toFixed(2)} outstanding`
                  : `${monthOptions.find(m => m.value === selectedMonthNum)?.label} ${selectedYear}`,
                icon: BanknotesIcon,
                textColor: 'text-green-600 dark:text-green-400',
              },
              {
                title: 'Deterministic Track',
                count: paymentData.deterministic ? `€${paymentData.deterministic.payments.trackTotal.toFixed(2)}` : '€0.00',
                note: paymentData.deterministic
                  ? (paymentData.deterministic.payments.outstandingPayment > 0
                    ? `€${paymentData.deterministic.payments.monthTotal.toFixed(2)} earned + €${paymentData.deterministic.payments.outstandingPayment.toFixed(2)} outstanding`
                    : 'Total payment')
                  : 'No data',
                icon: ChartBarIcon,
                textColor: 'text-brand-600 dark:text-brand-400',
                badge: paymentData.deterministic?.ranking?.league ? formatLeagueName(paymentData.deterministic.ranking.league) : null,
                badgeColor: paymentData.deterministic?.ranking?.league === 'elite' ? 'bg-yellow-100 text-yellow-800'
                  : paymentData.deterministic?.ranking?.league === 'challenger' ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-800',
              },
              {
                title: 'Probabilistic Track',
                count: paymentData.probabilistic ? `€${paymentData.probabilistic.payments.trackTotal.toFixed(2)}` : '€0.00',
                note: paymentData.probabilistic
                  ? (paymentData.probabilistic.payments.outstandingPayment > 0
                    ? `€${paymentData.probabilistic.payments.monthTotal.toFixed(2)} earned + €${paymentData.probabilistic.payments.outstandingPayment.toFixed(2)} outstanding`
                    : 'Total payment')
                  : 'No data',
                icon: TrophyIcon,
                textColor: 'text-brand-600 dark:text-brand-400',
                badge: paymentData.probabilistic?.ranking?.league ? formatLeagueName(paymentData.probabilistic.ranking.league) : null,
                badgeColor: paymentData.probabilistic?.ranking?.league === 'elite' ? 'bg-yellow-100 text-yellow-800'
                  : paymentData.probabilistic?.ranking?.league === 'challenger' ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-800',
              },
              {
                title: 'Participation Rate',
                count: `${paymentData.eligibility.participationRate}%`,
                note: `${paymentData.eligibility.daysSubmitted}/${paymentData.period.daysInMonth} days · ${paymentData.eligibility.daysMissed} missed`,
                icon: CalendarDaysIcon,
                textColor: paymentData.eligibility.participationRate >= 85
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400',
              },
            ]}
            columns={4}
          />

          {/* Info note about market participants */}
          <div className="mt-4 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <InformationCircleIcon className="h-4 w-4 flex-shrink-0" />
            <span>Rankings and payments are calculated based on market participants only (excludes fixed contract forecasters).</span>
          </div>

          {/* Two-column track breakdown */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TrackPaymentCard
              title="Deterministic (RMSE)"
              data={paymentData.deterministic ? {
                ...paymentData.deterministic,
                perHorizon: paymentData.perHorizon?.deterministic,
                minPayoutThreshold: paymentData.minPayoutThreshold,
              } : null}
            />
            <TrackPaymentCard
              title="Probabilistic (MWI)"
              data={paymentData.probabilistic ? {
                ...paymentData.probabilistic,
                perHorizon: paymentData.perHorizon?.probabilistic,
                minPayoutThreshold: paymentData.minPayoutThreshold,
              } : null}
            />
          </div>

          {/* Daily Details (Collapsible) */}
          {paymentData.dailyDetails && paymentData.dailyDetails.length > 0 && (
            <div className="mt-4 bg-surface dark:bg-gray-870 rounded-lg shadow-sm border border-gray-200/70 dark:border-gray-600/70 overflow-hidden">
              <button
                onClick={() => setShowDailyDetails(!showDailyDetails)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Daily Earnings Details</h3>
                {showDailyDetails ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                )}
              </button>

              {showDailyDetails && (
                <div className="border-t border-gray-100 dark:border-gray-700 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                    <thead className="bg-surface dark:bg-gray-870">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" colSpan="2">Deterministic (RMSE)</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" colSpan="2">Probabilistic (MWI)</th>
                      </tr>
                      <tr className="bg-surface dark:bg-gray-870">
                        <th className="px-4 py-2"></th>
                        <th className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Rank</th>
                        <th className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Reward</th>
                        <th className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Rank</th>
                        <th className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Reward</th>
                      </tr>
                    </thead>
                    <tbody className="bg-surface dark:bg-gray-870 divide-y divide-gray-200 dark:divide-gray-700">
                      {paymentData.dailyDetails.map((day, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                            {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </td>
                          <td className="px-3 py-3 text-center text-gray-700 dark:text-gray-300">
                            {day.deterministic?.rank != null ? `#${day.deterministic.rank}` : '—'}
                          </td>
                          <td className="px-3 py-3 text-center text-gray-700 dark:text-gray-300">
                            {day.deterministic?.dailyReward != null ? `€${day.deterministic.dailyReward.toFixed(2)}` : '—'}
                          </td>
                          <td className="px-3 py-3 text-center text-gray-700 dark:text-gray-300">
                            {day.probabilistic?.rank != null ? `#${day.probabilistic.rank}` : '—'}
                          </td>
                          <td className="px-3 py-3 text-center text-gray-700 dark:text-gray-300">
                            {day.probabilistic?.dailyReward != null ? `€${day.probabilistic.dailyReward.toFixed(2)}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
